import React, { useState } from 'react';
import { MessageCircle, Instagram, Send, Mail, X } from 'lucide-react';

/**
 * Floating multi-channel contact buttons (WhatsApp / Instagram / Telegram / Email).
 *
 * Behavior:
 *  - If only one channel is enabled → render that single button directly.
 *  - If multiple are enabled → render a primary toggle (WhatsApp if enabled, else first)
 *    and the rest expand vertically above it on click.
 */
const ContactButtons = ({
  whatsapp = { enabled: false, number: '', message: '', label: 'WhatsApp' },
  instagram = { enabled: false, username: '', label: 'Instagram' },
  telegram = { enabled: false, username: '', label: 'Telegram' },
  email = { enabled: false, address: '', subject: '', label: 'Email' },
}) => {
  const [open, setOpen] = useState(false);

  // Build active channels
  const channels = [];
  // WhatsApp
  const waDigits = String(whatsapp.number || '').replace(/[^\d]/g, '');
  if (whatsapp.enabled && waDigits) {
    channels.push({
      key: 'whatsapp',
      label: whatsapp.label || 'WhatsApp',
      href: `https://wa.me/${waDigits}${whatsapp.message ? `?text=${encodeURIComponent(whatsapp.message)}` : ''}`,
      icon: <MessageCircle size={22} />,
      bg: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
      shadow: 'rgba(37, 211, 102, 0.5)',
    });
  }
  // Instagram
  const igUser = String(instagram.username || '').trim().replace(/^@/, '');
  if (instagram.enabled && igUser) {
    channels.push({
      key: 'instagram',
      label: instagram.label || 'Instagram',
      href: `https://instagram.com/${encodeURIComponent(igUser)}`,
      icon: <Instagram size={22} />,
      bg: 'linear-gradient(135deg, #f58529 0%, #dd2a7b 45%, #8134af 80%, #515bd4 100%)',
      shadow: 'rgba(221, 42, 123, 0.5)',
    });
  }
  // Telegram
  const tgUser = String(telegram.username || '').trim().replace(/^@/, '');
  if (telegram.enabled && tgUser) {
    channels.push({
      key: 'telegram',
      label: telegram.label || 'Telegram',
      href: `https://t.me/${encodeURIComponent(tgUser)}`,
      icon: <Send size={22} />,
      bg: 'linear-gradient(135deg, #229ED9 0%, #1a7bb0 100%)',
      shadow: 'rgba(34, 158, 217, 0.5)',
    });
  }
  // Email
  const emailAddr = String(email.address || '').trim();
  if (email.enabled && emailAddr) {
    const subjectQS = email.subject ? `?subject=${encodeURIComponent(email.subject)}` : '';
    channels.push({
      key: 'email',
      label: email.label || 'Email',
      href: `mailto:${emailAddr}${subjectQS}`,
      icon: <Mail size={22} />,
      bg: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
      shadow: 'rgba(99, 102, 241, 0.5)',
    });
  }

  if (channels.length === 0) return null;

  // Single channel — render flat button
  if (channels.length === 1) {
    const c = channels[0];
    return (
      <a
        href={c.href}
        target={c.key === 'email' ? '_self' : '_blank'}
        rel="noopener noreferrer"
        className="contact-fab contact-fab-solo"
        data-testid={`contact-fab-${c.key}`}
        aria-label={c.label}
        title={c.label}
        style={{ background: c.bg, boxShadow: `0 8px 24px ${c.shadow}, 0 2px 6px rgba(0,0,0,0.3)` }}
      >
        <span className="contact-fab-icon">{c.icon}</span>
        <span className="contact-fab-label">{c.label}</span>
      </a>
    );
  }

  // Multi-channel — stacked FABs that expand on toggle
  const primary = channels[0];
  const rest = channels.slice(1);

  return (
    <div className="contact-fab-stack" data-testid="contact-fab-stack">
      {open && rest.map((c, idx) => (
        <a
          key={c.key}
          href={c.href}
          target={c.key === 'email' ? '_self' : '_blank'}
          rel="noopener noreferrer"
          className="contact-fab contact-fab-mini"
          data-testid={`contact-fab-${c.key}`}
          aria-label={c.label}
          title={c.label}
          style={{
            background: c.bg,
            boxShadow: `0 6px 16px ${c.shadow}, 0 2px 4px rgba(0,0,0,0.25)`,
            animationDelay: `${idx * 0.04}s`,
          }}
        >
          <span className="contact-fab-icon">{c.icon}</span>
          <span className="contact-fab-label">{c.label}</span>
        </a>
      ))}
      <div className="contact-fab-primary-wrap">
        <a
          href={primary.href}
          target={primary.key === 'email' ? '_self' : '_blank'}
          rel="noopener noreferrer"
          className="contact-fab contact-fab-primary"
          data-testid={`contact-fab-${primary.key}`}
          aria-label={primary.label}
          title={primary.label}
          style={{ background: primary.bg, boxShadow: `0 8px 24px ${primary.shadow}, 0 2px 6px rgba(0,0,0,0.3)` }}
        >
          <span className="contact-fab-icon">{primary.icon}</span>
          <span className="contact-fab-label">{primary.label}</span>
        </a>
        <button
          type="button"
          className={`contact-fab-toggle ${open ? 'contact-fab-toggle-open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="contact-fab-toggle"
          aria-label={open ? 'Bağla' : 'Daha çox əlaqə kanalı'}
          title={open ? 'Bağla' : `+${rest.length} əlaqə`}
        >
          {open ? <X size={14} /> : `+${rest.length}`}
        </button>
      </div>
    </div>
  );
};

export default ContactButtons;
